import { EnterOutlined } from '@ant-design/icons';
import type { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';

import { Divider, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Input from '@/components/Inputs/Input';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import { radius } from '@/styles/sizes';
import { TOPIC_2_TYPE } from '@/types/taxonomyConst';
import { isEmptyString, timeOut } from '@/utils/util';

import S from '../styles';
import AddedItem from './AddedItem';
import type { CandidateItemT } from './types';

type Props = {
  selectedItems: CandidateItemT[];
  setSelectedItems: Dispatch<SetStateAction<CandidateItemT[]>>;
  onCancelClick: () => void;
  onNextClick: () => void;
};

const SelectAiTopic = (props: Props) => {
  const color = useColor();

  return (
    <Style g={16}>
      <S.MainLayout style={{ width: 778, overflow: 'auto' }}>
        <VFlex className="header">
          <Heading type="h2">Add AI topics</Heading>
        </VFlex>
        <Divider />
        <VFlex className="body" g={16} style={{ overflow: 'auto' }}>
          <VFlex g={24}>
            <VFlex g={14}>
              <Typo type="label1" weight="sb">
                Add your own
              </Typo>
              <Input
                placeholder="Type topic name"
                sizeType="l"
                wrapperStyle={{ width: 324 }}
                onEnter={async (value, e) => {
                  const target = e.target as HTMLInputElement;
                  if (isEmptyString(value)) {
                    await timeOut(100);
                    target.focus();
                    return;
                  }
                  const newArr = [...props.selectedItems];
                  newArr.push({
                    name: value,
                    category: TOPIC_2_TYPE.AI,
                    ai_rule: {
                      prompt: '',
                      prompt_en: '',
                      filter: undefined,
                    },
                    readyState: false,
                    promptChanged: false,
                    isError: false,
                    isLoading: false,
                  });
                  props.setSelectedItems(newArr);

                  target.value = '';
                  await timeOut(100);
                  target.focus();
                }}
                rightIcon={
                  <IconButton
                    icon={<EnterOutlined style={{ color: color.gray500 }} />}
                  />
                }
              />
            </VFlex>
          </VFlex>
          <SelectedArea f={1}>
            <Flex j="space-between">
              <Typo type="label1" weight="m" className="text-primary-100">
                {props.selectedItems.length} added
              </Typo>
            </Flex>
            <Space h={12} />
            <VFlex className="list-box" f={1}>
              {props.selectedItems.length > 0 && (
                <Flex g={8} style={{ flexWrap: 'wrap' }}>
                  {props.selectedItems.map((item, index) => (
                    <AddedItem
                      key={index}
                      name={item.name}
                      onDeleteClick={() => {
                        const newArr = [...props.selectedItems];
                        newArr.splice(index, 1);
                        props.setSelectedItems(newArr);
                      }}
                    />
                  ))}
                </Flex>
              )}
            </VFlex>
          </SelectedArea>
        </VFlex>
        <Divider />
        <Flex className="footer" j="flex-end">
          <Button variant="ghost" sizeType="l" onClick={props.onCancelClick}>
            Cancel
          </Button>
          <Button
            disabled={props.selectedItems.length === 0}
            variant="primary"
            sizeType="l"
            onClick={props.onNextClick}
          >
            Next
          </Button>
        </Flex>
      </S.MainLayout>
    </Style>
  );
};

const Style = styled(Flex)`
  width: 800px;
  height: calc(100vh - 64px);
  max-height: 780px;
  overflow: auto;
`;

const SelectedArea = styled(VFlex)`
  padding: 24px;
  padding-bottom: 0;
  padding-right: 0;
  background-color: ${(props) => props.theme.colors.gray200};
  border-radius: ${radius.card}px;
  overflow: auto;
  .list-box {
    overflow: auto;
    padding: 12px 24px 24px 0px;
  }
`;

export default SelectAiTopic;
